import CircularLoader from 'components/CircularLoader';
import { GLOBAL_SEARCH_ENTITY_ICON } from 'components/global-search/entityDisplay';
import {
    MessageCircleIcon,
    SearchIcon,
    type SvgIconComponent,
    TrendingUpIcon,
    UserIcon,
    WalletIcon,
    XIcon,
} from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { Button } from 'components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { Tooltip } from 'components/ui/tooltip';
import { FOCUS_RING_INSET } from 'layout/NewDesignLayout/focusRing';
import React, { UIEvent, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useGetAiChatThreadsQuery } from 'store/api/aiChatApi';
import { AiChatThreadSummary } from 'types/aiChat';
import { cn } from 'utils/cn';
import { formatTimeOfDay } from 'utils/date';
import { NEUTRAL_TILE_CLASS } from 'utils/families';
import { foldForSearch } from 'utils/foldForSearch';
import { useAiChatStreamingThreadIds } from '../AiChatSessionProvider';
import { promptIconFor } from '../floating/pagePrompts';
import { isModifiedClick } from './message/isModifiedClick';
import { groupThreadsByDate, ThreadDateGroup } from '../utils/threadDateGroups';

interface AiChatThreadListProps {
    selectedThreadId: number | null;
    /** What a pick does; with `threadPath` the row is a link there and this runs as it is followed. */
    onSelectThread: (threadId: number) => void;
    /** A conversation's address, where the list's page opens it: its rows are links to it. */
    threadPath?: (threadId: number) => string;
    // Omitted where deleting is not offered, which hides the per-row delete button rather than
    // rendering one that does nothing.
    onDeleteThread?: (threadId: number) => void;
}

const PAGE_SIZE = 20;

const GROUP_LABEL_ID: Record<ThreadDateGroup, string> = {
    today: 'ai-chat.history.today',
    yesterday: 'ai-chat.history.yesterday',
    'last-7-days': 'ai-chat.history.last-7-days',
    older: 'ai-chat.history.older',
};

/** The icon of each kind the AI files a chat under; any other kind reads as a general chat. */
const KIND_ICON = new Map<string, SvgIconComponent>([
    ['case', GLOBAL_SEARCH_ENTITY_ICON.MATTER],
    ['contact', GLOBAL_SEARCH_ENTITY_ICON.PARTY],
    ['bd', GLOBAL_SEARCH_ENTITY_ICON.EFFORT],
    ['finance', WalletIcon],
    ['timecards', GLOBAL_SEARCH_ENTITY_ICON.TIMECARD],
    ['people', UserIcon],
    ['chart', TrendingUpIcon],
]);

/** The starting prompt's icon while the prompt lists still hold it, else the kind's. */
const threadIcon = ({ promptId, kind }: AiChatThreadSummary) => {
    const Icon = (promptId ? promptIconFor(promptId) : undefined) ?? KIND_ICON.get(kind ?? '') ?? MessageCircleIcon;
    return <Icon className="size-4" />;
};

const AiChatThreadList: React.FC<AiChatThreadListProps> = ({
    selectedThreadId,
    onSelectThread,
    threadPath,
    onDeleteThread,
}) => {
    const intl = useIntl();
    const groupId = useId();
    const [page, setPage] = useState(0);
    const [query, setQuery] = useState('');
    const { data: threads, isLoading, isFetching, error } = useGetAiChatThreadsQuery(page);
    const writingThreadIds = useAiChatStreamingThreadIds();
    const searchLabel = intl.formatMessage({ id: 'ai-chat.history.search' });

    // A full last page suggests there are more; a short one is the end, which stops the requests.
    const hasMore = !isFetching && threads != null && threads.length >= (page + 1) * PAGE_SIZE;

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
        const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 50 && hasMore) {
            setPage((prev) => prev + 1);
        }
    };

    const search = foldForSearch(query.trim());
    const shownThreads = search
        ? threads?.filter((thread) =>
              foldForSearch(`${thread.title ?? ''} ${thread.description ?? ''}`).includes(search)
          )
        : threads;
    const matchCount = shownThreads?.length ?? 0;

    // The search covers the loaded pages, and a few matches leave nothing to scroll that would load more: it asks for
    // the next page itself, as each one lands, until a page of matches shows or every chat was searched.
    if (search && hasMore && matchCount < PAGE_SIZE) {
        setPage(page + 1);
    }

    return (
        <div className="bg-card flex h-full flex-col">
            <div className="px-3 pt-3 pb-1">
                <InputGroup className="border-border-control bg-card shadow-none">
                    <InputGroupAddon>
                        <SearchIcon className="text-foreground-tertiary size-4" />
                    </InputGroupAddon>
                    <InputGroupInput
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={searchLabel}
                        aria-label={searchLabel}
                        className="text-foreground md:text-meta"
                    />
                </InputGroup>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2" onScroll={handleScroll}>
                {/* Initial Loading State */}
                {isLoading && page === 0 && <CircularLoader className="static p-6 [&>span]:size-6" />}

                {error && (
                    <p className="text-meta text-danger-ink p-4 text-center">
                        {intl.formatMessage({
                            id: 'ai-chat.error.loading-threads',
                            defaultMessage: 'Failed to load chats',
                        })}
                    </p>
                )}

                {!isLoading && threads?.length === 0 && (
                    <p className="text-meta text-muted-foreground p-6 text-center">
                        {intl.formatMessage({ id: 'ai-chat.no-threads', defaultMessage: 'No previous chats' })}
                    </p>
                )}

                {search && threads?.length && matchCount === 0 && !hasMore && !isFetching ? (
                    <p className="text-meta text-muted-foreground p-6 text-center">
                        {intl.formatMessage({ id: 'no-results' })}
                    </p>
                ) : null}

                {groupThreadsByDate(shownThreads ?? []).map(({ group, threads: groupThreads }) => (
                    <div key={group}>
                        <h3
                            id={`${groupId}-${group}`}
                            className="text-label text-muted-foreground px-2.5 pt-3 pb-1 uppercase"
                        >
                            {intl.formatMessage({ id: GROUP_LABEL_ID[group] })}
                        </h3>
                        <ul aria-labelledby={`${groupId}-${group}`}>
                            {groupThreads.map((thread) => (
                                <ThreadListItem
                                    key={thread.id}
                                    thread={thread}
                                    group={group}
                                    isSelected={selectedThreadId === thread.id}
                                    isWriting={writingThreadIds.includes(thread.id)}
                                    to={threadPath?.(thread.id)}
                                    onSelect={() => onSelectThread(thread.id)}
                                    onDelete={onDeleteThread && (() => onDeleteThread(thread.id))}
                                />
                            ))}
                        </ul>
                    </div>
                ))}

                {/* Load More Indicator */}
                {isFetching && page > 0 && <CircularLoader className="static p-4 [&>span]:size-5" />}
            </div>
        </div>
    );
};

interface ThreadListItemProps {
    thread: AiChatThreadSummary;
    group: ThreadDateGroup;
    isSelected: boolean;
    isWriting: boolean;
    /** The conversation's address, which makes the row a link to it. */
    to?: string;
    onSelect: () => void;
    onDelete?: () => void;
}

const ThreadListItem: React.FC<ThreadListItemProps> = ({
    thread,
    group,
    isSelected,
    isWriting,
    to,
    onSelect,
    onDelete,
}) => {
    const intl = useIntl();
    const deleteLabel = intl.formatMessage({ id: 'ai-chat.delete-thread', defaultMessage: 'Delete chat' });
    const writingLabel = intl.formatMessage({ id: 'ai-chat.history.writing' });
    const { lastMessageAt } = thread;

    // Today and yesterday by the clock, the past week by weekday, older by the firm's date format.
    let time: React.ReactNode = null;
    if (lastMessageAt && group === 'older') {
        time = <FormattedDate value={lastMessageAt} fallback={null} />;
    } else if (lastMessageAt && group === 'last-7-days') {
        time = intl.formatDate(lastMessageAt, { weekday: 'short' });
    } else if (lastMessageAt) {
        time = formatTimeOfDay(lastMessageAt);
    }

    const rowProps = {
        onClick: (event: React.MouseEvent) => {
            // A modified click on the link opens the conversation elsewhere and leaves this page as it is.
            if (to && isModifiedClick(event)) return;
            onSelect();
        },
        'aria-current': isSelected || undefined,
        className: cn(
            'flex w-full cursor-pointer items-start gap-3 rounded-lg px-2.5 py-2 text-start transition-colors',
            FOCUS_RING_INSET,
            isSelected ? 'bg-primary-wash text-primary-ink' : 'text-foreground hover:bg-row-stripe',
            // Without a pointer the delete button stands at the row's end for good, so the row
            // leaves it room; a tap there would otherwise land on the delete prompt.
            onDelete && 'pe-12 pointer-fine:pe-2.5'
        ),
    };
    const content = (
        <>
            <span className={cn('grid size-8 shrink-0 place-items-center rounded-[9px]', NEUTRAL_TILE_CLASS)}>
                {threadIcon(thread)}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-meta font-medium text-pretty">
                    {thread.title ||
                        intl.formatMessage({
                            id: 'ai-chat.untitled',
                            defaultMessage: 'Untitled Chat',
                        })}
                </span>
                {isWriting ? (
                    <span className="text-caption">
                        <span className="t-shimmer" data-text={writingLabel}>
                            {writingLabel}
                        </span>
                    </span>
                ) : null}
                {!isWriting && thread.description ? (
                    <span className="text-caption text-foreground-tertiary text-pretty">{thread.description}</span>
                ) : null}
            </span>
            {time ? (
                <time
                    dateTime={lastMessageAt}
                    className={cn(
                        'text-caption text-muted-foreground shrink-0 tabular-nums',
                        // Where a pointer reveals the delete button, it takes the time's place.
                        onDelete &&
                            'pointer-fine:group-hover:invisible pointer-fine:group-has-[:focus-visible]:invisible'
                    )}
                >
                    {time}
                </time>
            ) : null}
        </>
    );

    return (
        // `group` drives the delete button's hover reveal where there is a pointer to hover with.
        <li className="group relative">
            {to ? (
                <Link to={to} {...rowProps}>
                    {content}
                </Link>
            ) : (
                <button type="button" {...rowProps}>
                    {content}
                </button>
            )}

            {onDelete ? (
                <Tooltip content={deleteLabel}>
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={deleteLabel}
                        onClick={onDelete}
                        // Revealed on hover where there is a pointer to hover with, and while the keyboard
                        // is on the row (a click leaves the time in place). Hidden by opacity alone, so it
                        // stays in the Tab order whatever the pointer did last. Gated on the pointer rather
                        // than the viewport, because a tablet is wide and still has no hover: there the
                        // button stands permanently instead of being unreachable.
                        className="text-foreground-tertiary hover:text-danger-ink absolute end-1 top-1 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-fine:group-has-[:focus-visible]:opacity-100"
                    >
                        <XIcon className="size-4" />
                    </Button>
                </Tooltip>
            ) : null}
        </li>
    );
};

export default AiChatThreadList;
